/**
 * Datacenter Industry Configuration
 *
 * Comprehensive configuration for datacenter operations, server management,
 * infrastructure monitoring, and capacity planning.
 *
 * Key Features:
 * - Server and rack health monitoring
 * - Power Usage Effectiveness (PUE) optimization
 * - Capacity planning and forecasting
 * - Cooling efficiency management
 * - SLA compliance tracking
 * - Hardware lifecycle management
 */

import { IndustryConfig } from '../industryTypes'

export const datacenterConfig: IndustryConfig = {
  id: 'datacenter',
  name: 'Datacenter',
  description: 'Infrastructure monitoring and optimization for datacenters',
  icon: 'Server',

  terminology: {
    asset: 'Server',
    assets: 'Servers',
    assetPlural: 'Servers',
    assetType: 'Hardware Type',
    failure: 'System Failure',
    health: 'System Health',
    healthScore: 'Health Score',
    healthy: 'Operational',
    degraded: 'Performance Degraded',
    critical: 'Critical Alert',
    alert: 'Infrastructure Alert',
    process: 'Workload',
    maintenance: 'Scheduled Maintenance',
    inspection: 'Health Check',
    downtime: 'Outage',
    workOrder: 'Maintenance Ticket',
    prediction: 'Capacity Forecast',
    anomaly: 'Performance Anomaly',
    trend: 'Resource Trend',
    risk: 'Uptime Risk',
    facility: 'Datacenter',
    facilityPlural: 'Datacenters',
    location: 'Rack',
    operator: 'Infrastructure Engineer',
    supervisor: 'Datacenter Manager',
    calibration: 'System Calibration',
    compliance: 'SLA Compliance',
    criticality: 'Tier Level',
  },

  assetTypes: [
    {
      id: 'compute_server',
      name: 'Compute Server',
      icon: 'Server',
      description: 'Physical or virtual servers for compute workloads',
      category: 'Compute',
      defaultMetrics: ['cpu_utilization', 'memory_utilization', 'disk_io', 'network_throughput', 'temperature'],
      maintenanceTypes: ['firmware_update', 'hardware_replacement', 'os_patching', 'performance_tuning', 'decommission'],
      criticalityLevels: [
        { id: 'tier_1', name: 'Tier 1 Production', color: '#ef4444', description: 'Mission-critical production systems', downtimeCostMultiplier: 1000 },
        { id: 'tier_2', name: 'Tier 2 Production', color: '#f59e0b', description: 'High-priority production', downtimeCostMultiplier: 500 },
        { id: 'tier_3', name: 'Tier 3 Non-Prod', color: '#3b82f6', description: 'Development and staging', downtimeCostMultiplier: 50 },
        { id: 'tier_4', name: 'Tier 4 Test', color: '#6b7280', description: 'Testing and sandbox', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'storage_system',
      name: 'Storage System',
      icon: 'Database',
      description: 'SAN, NAS, and object storage systems',
      category: 'Storage',
      defaultMetrics: ['capacity_utilization', 'iops', 'latency', 'throughput', 'error_rate'],
      maintenanceTypes: ['disk_replacement', 'firmware_update', 'capacity_expansion', 'performance_optimization'],
      criticalityLevels: [
        { id: 'primary', name: 'Primary Storage', color: '#ef4444', description: 'Mission-critical data', downtimeCostMultiplier: 2000 },
        { id: 'secondary', name: 'Secondary Storage', color: '#f59e0b', description: 'Important data', downtimeCostMultiplier: 500 },
        { id: 'archival', name: 'Archival Storage', color: '#3b82f6', description: 'Long-term archival', downtimeCostMultiplier: 50 },
      ],
    },
    {
      id: 'network_switch',
      name: 'Network Switch',
      icon: 'Network',
      description: 'Core, distribution, and access layer switches',
      category: 'Network',
      defaultMetrics: ['bandwidth_utilization', 'packet_loss', 'latency', 'error_rate', 'port_utilization'],
      maintenanceTypes: ['firmware_update', 'configuration_change', 'hardware_replacement', 'cable_management'],
      criticalityLevels: [
        { id: 'core', name: 'Core Switch', color: '#ef4444', description: 'Core network backbone', downtimeCostMultiplier: 5000 },
        { id: 'distribution', name: 'Distribution', color: '#f59e0b', description: 'Distribution layer', downtimeCostMultiplier: 1000 },
        { id: 'access', name: 'Access', color: '#3b82f6', description: 'Access layer', downtimeCostMultiplier: 200 },
      ],
    },
    {
      id: 'cooling_unit',
      name: 'Cooling Unit',
      icon: 'Wind',
      description: 'HVAC and cooling infrastructure (CRAC/CRAH)',
      category: 'Facilities',
      defaultMetrics: ['temperature', 'humidity', 'airflow', 'power_consumption', 'efficiency_cop'],
      maintenanceTypes: ['filter_replacement', 'refrigerant_service', 'calibration', 'cleaning'],
      criticalityLevels: [
        { id: 'primary', name: 'Primary Cooling', color: '#ef4444', description: 'Primary cooling system', downtimeCostMultiplier: 3000 },
        { id: 'redundant', name: 'Redundant Cooling', color: '#3b82f6', description: 'Redundant/backup cooling', downtimeCostMultiplier: 500 },
      ],
    },
    {
      id: 'power_system',
      name: 'Power Distribution Unit',
      icon: 'Zap',
      description: 'PDUs, UPS, and power distribution infrastructure',
      category: 'Power',
      defaultMetrics: ['power_draw', 'voltage', 'current', 'power_factor', 'temperature'],
      maintenanceTypes: ['battery_replacement', 'load_testing', 'cleaning', 'firmware_update'],
      criticalityLevels: [
        { id: 'primary', name: 'Primary Power', color: '#ef4444', description: 'Primary power feed', downtimeCostMultiplier: 10000 },
        { id: 'ups', name: 'UPS System', color: '#f59e0b', description: 'Uninterruptible power supply', downtimeCostMultiplier: 5000 },
        { id: 'pdu', name: 'Rack PDU', color: '#3b82f6', description: 'Rack power distribution', downtimeCostMultiplier: 1000 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'uptime_reliability',
      name: 'Uptime & Reliability',
      icon: 'Activity',
      description: 'System uptime and reliability metrics',
      category: 'universal',
      kpis: [
        {
          id: 'uptime_percentage',
          name: 'Uptime Percentage',
          unit: '%',
          description: 'System availability percentage',
          thresholds: {
            critical: { min: 0, max: 99.9 },
            warning: { min: 99.9, max: 99.99 },
            normal: { min: 99.99, max: 100 },
          },
          target: 99.99,
          higherIsBetter: true,
        },
        {
          id: 'mtbf',
          name: 'Mean Time Between Failures',
          unit: 'hours',
          description: 'Average time between system failures',
          thresholds: {
            critical: { min: 0, max: 1000 },
            warning: { min: 1000, max: 5000 },
            normal: { min: 5000, max: 100000 },
          },
          target: 10000,
          higherIsBetter: true,
        },
        {
          id: 'mttr',
          name: 'Mean Time To Repair',
          unit: 'hours',
          description: 'Average time to repair and restore service',
          thresholds: {
            critical: { min: 4, max: 100 },
            warning: { min: 1, max: 4 },
            normal: { min: 0, max: 1 },
          },
          target: 0.5,
          higherIsBetter: false,
        },
      ],
    },
    {
      id: 'resource_utilization',
      name: 'Resource Utilization',
      icon: 'Gauge',
      description: 'CPU, memory, storage, and network utilization',
      category: 'industry_specific',
      kpis: [
        {
          id: 'cpu_utilization',
          name: 'CPU Utilization',
          unit: '%',
          description: 'Average CPU utilization across fleet',
          thresholds: {
            critical: { min: 90, max: 100 },
            warning: { min: 75, max: 90 },
            normal: { min: 0, max: 75 },
          },
          target: 70,
          higherIsBetter: false,
        },
        {
          id: 'memory_utilization',
          name: 'Memory Utilization',
          unit: '%',
          description: 'Average memory utilization',
          thresholds: {
            critical: { min: 95, max: 100 },
            warning: { min: 85, max: 95 },
            normal: { min: 0, max: 85 },
          },
          target: 75,
          higherIsBetter: false,
        },
        {
          id: 'storage_utilization',
          name: 'Storage Utilization',
          unit: '%',
          description: 'Storage capacity utilization',
          thresholds: {
            critical: { min: 90, max: 100 },
            warning: { min: 80, max: 90 },
            normal: { min: 0, max: 80 },
          },
          target: 70,
          higherIsBetter: false,
        },
      ],
    },
    {
      id: 'energy_efficiency',
      name: 'Energy Efficiency',
      icon: 'Zap',
      description: 'Power consumption and efficiency metrics',
      category: 'industry_specific',
      kpis: [
        {
          id: 'pue',
          name: 'Power Usage Effectiveness',
          unit: 'ratio',
          description: 'Total facility power / IT equipment power',
          thresholds: {
            critical: { min: 2.0, max: 10 },
            warning: { min: 1.5, max: 2.0 },
            normal: { min: 1.0, max: 1.5 },
          },
          target: 1.2,
          higherIsBetter: false,
        },
        {
          id: 'dcie',
          name: 'Data Center Infrastructure Efficiency',
          unit: '%',
          description: 'IT equipment power / Total facility power',
          thresholds: {
            critical: { min: 0, max: 50 },
            warning: { min: 50, max: 67 },
            normal: { min: 67, max: 100 },
          },
          target: 80,
          higherIsBetter: true,
        },
        {
          id: 'cooling_efficiency',
          name: 'Cooling Efficiency (COP)',
          unit: 'ratio',
          description: 'Coefficient of Performance for cooling systems',
          thresholds: {
            critical: { min: 0, max: 2.0 },
            warning: { min: 2.0, max: 3.0 },
            normal: { min: 3.0, max: 10 },
          },
          target: 4.0,
          higherIsBetter: true,
        },
      ],
    },
    {
      id: 'capacity_planning',
      name: 'Capacity Planning',
      icon: 'TrendingUp',
      description: 'Capacity growth and forecasting metrics',
      category: 'industry_specific',
      kpis: [
        {
          id: 'capacity_remaining',
          name: 'Capacity Remaining',
          unit: 'days',
          description: 'Days until capacity exhaustion',
          thresholds: {
            critical: { min: 0, max: 30 },
            warning: { min: 30, max: 90 },
            normal: { min: 90, max: 1000 },
          },
          target: 180,
          higherIsBetter: true,
        },
        {
          id: 'growth_rate',
          name: 'Resource Growth Rate',
          unit: '%/month',
          description: 'Monthly resource consumption growth',
          thresholds: {
            critical: { min: 10, max: 100 },
            warning: { min: 5, max: 10 },
            normal: { min: 0, max: 5 },
          },
          target: 3,
          higherIsBetter: false,
        },
      ],
    },
    {
      id: 'performance',
      name: 'Performance',
      icon: 'Zap',
      description: 'System performance and response time',
      category: 'universal',
      kpis: [
        {
          id: 'latency',
          name: 'Network Latency',
          unit: 'ms',
          description: 'Average network latency',
          thresholds: {
            critical: { min: 10, max: 1000 },
            warning: { min: 5, max: 10 },
            normal: { min: 0, max: 5 },
          },
          target: 1,
          higherIsBetter: false,
        },
        {
          id: 'iops',
          name: 'Storage IOPS',
          unit: 'IOPS',
          description: 'Input/output operations per second',
          thresholds: {
            critical: { min: 0, max: 1000 },
            warning: { min: 1000, max: 5000 },
            normal: { min: 5000, max: 1000000 },
          },
          target: 10000,
          higherIsBetter: true,
        },
      ],
    },
  ],

  maintenanceTypes: [
    {
      id: 'firmware_update',
      name: 'Firmware Update',
      icon: 'Download',
      description: 'Update firmware on servers, switches, and storage',
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      requiredSkills: ['firmware_management', 'change_control'],
      downtime: true,
      criticality: 'high',
    },
    {
      id: 'hardware_replacement',
      name: 'Hardware Replacement',
      icon: 'Wrench',
      description: 'Replace failed or aging hardware components',
      estimatedDuration: { min: 60, typical: 120, max: 240, unit: 'minutes' },
      requiredSkills: ['hardware_installation', 'cabling'],
      downtime: true,
      criticality: 'critical',
    },
    {
      id: 'capacity_expansion',
      name: 'Capacity Expansion',
      icon: 'TrendingUp',
      description: 'Add new servers, storage, or network capacity',
      estimatedDuration: { min: 120, typical: 480, max: 960, unit: 'minutes' },
      requiredSkills: ['capacity_planning', 'deployment_automation'],
      downtime: false,
      criticality: 'medium',
    },
    {
      id: 'performance_tuning',
      name: 'Performance Tuning',
      icon: 'Settings',
      description: 'Optimize system performance and configuration',
      estimatedDuration: { min: 60, typical: 180, max: 360, unit: 'minutes' },
      requiredSkills: ['performance_analysis', 'system_tuning'],
      downtime: false,
      criticality: 'medium',
    },
    {
      id: 'security_patching',
      name: 'Security Patching',
      icon: 'Shield',
      description: 'Apply security patches and updates',
      estimatedDuration: { min: 30, typical: 90, max: 180, unit: 'minutes' },
      requiredSkills: ['patch_management', 'security'],
      downtime: true,
      criticality: 'high',
    },
    {
      id: 'cooling_maintenance',
      name: 'Cooling System Maintenance',
      icon: 'Wind',
      description: 'Service HVAC, CRAC, and cooling systems',
      estimatedDuration: { min: 120, typical: 240, max: 480, unit: 'minutes' },
      requiredSkills: ['hvac', 'facilities_management'],
      downtime: false,
      criticality: 'high',
    },
    {
      id: 'power_maintenance',
      name: 'Power System Maintenance',
      icon: 'Zap',
      description: 'Service UPS, PDUs, and power infrastructure',
      estimatedDuration: { min: 60, typical: 180, max: 360, unit: 'minutes' },
      requiredSkills: ['electrical', 'power_systems'],
      downtime: false,
      criticality: 'critical',
    },
  ],

  riskFactors: [
    {
      id: 'hardware_age',
      name: 'Hardware Age',
      description: 'Risk due to aging hardware past warranty or EOL',
      severity: 'high',
      calculation: 'age_based',
    },
    {
      id: 'capacity_saturation',
      name: 'Capacity Saturation',
      description: 'Risk of running out of capacity (CPU, memory, storage)',
      severity: 'critical',
      calculation: 'threshold_based',
    },
    {
      id: 'thermal_risk',
      name: 'Thermal Risk',
      description: 'Risk of overheating due to cooling issues',
      severity: 'critical',
      calculation: 'sensor_based',
    },
    {
      id: 'single_point_failure',
      name: 'Single Point of Failure',
      description: 'Critical systems without redundancy',
      severity: 'critical',
      calculation: 'configuration_based',
    },
    {
      id: 'patch_compliance',
      name: 'Patch Compliance Risk',
      description: 'Security risk from missing patches',
      severity: 'high',
      calculation: 'compliance_based',
    },
    {
      id: 'power_efficiency',
      name: 'Power Inefficiency',
      description: 'Risk of high operating costs from poor PUE',
      severity: 'medium',
      calculation: 'efficiency_based',
    },
    {
      id: 'sla_breach_risk',
      name: 'SLA Breach Risk',
      description: 'Risk of violating uptime SLAs',
      severity: 'critical',
      calculation: 'predictive',
    },
  ],

  complianceStandards: [
    {
      id: 'tier_classification',
      name: 'Tier Classification (Uptime Institute)',
      description: 'Datacenter tier standards (Tier I-IV)',
      authority: 'Uptime Institute',
      criticality: 'high',
    },
    {
      id: 'iso_27001',
      name: 'ISO/IEC 27001',
      description: 'Information security management systems',
      authority: 'ISO',
      criticality: 'high',
    },
    {
      id: 'soc_2',
      name: 'SOC 2 Type II',
      description: 'Service Organization Controls for security, availability, processing integrity',
      authority: 'AICPA',
      criticality: 'high',
    },
    {
      id: 'energy_star',
      name: 'Energy Star Certification',
      description: 'Energy efficiency standards for datacenters',
      authority: 'EPA',
      criticality: 'medium',
    },
    {
      id: 'ashrae',
      name: 'ASHRAE Thermal Guidelines',
      description: 'Temperature and humidity recommendations',
      authority: 'ASHRAE',
      criticality: 'medium',
    },
    {
      id: 'hipaa',
      name: 'HIPAA Compliance',
      description: 'Healthcare data protection (if applicable)',
      authority: 'HHS',
      criticality: 'critical',
    },
    {
      id: 'pci_dss',
      name: 'PCI DSS',
      description: 'Payment card industry security standards (if applicable)',
      authority: 'PCI SSC',
      criticality: 'critical',
    },
  ],

  // =============================================================================
  // NARRATIVE TEMPLATES
  // =============================================================================
  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Infrastructure Health Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Infrastructure Prediction Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'AI {prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} telemetry data points. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
    {
      id: 'capacity_forecast',
      name: 'Capacity Planning Forecast',
      context: 'prediction',
      audience: ['executive', 'manager'],
      template: 'Capacity analysis for {facility}: Current utilization at {utilization}%. At current growth rate of {growthRate}%/month, capacity will be reached in {timeToCapacity}. Recommended: {recommendation}',
      variables: [
        { name: 'facility', type: 'terminology' },
        { name: 'utilization', type: 'percentage' },
        { name: 'growthRate', type: 'metric' },
        { name: 'timeToCapacity', type: 'time' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '7d',
    refreshInterval: 30,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Datacenter-Specific)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'datacenter_engineer',
      category: 'sme',
      name: 'Datacenter Engineer',
      shortName: 'DCE',
      description: 'Designs and maintains datacenter infrastructure for optimal performance and availability',
      responsibilities: [
        'Infrastructure capacity planning',
        'Power and cooling optimization',
        'Hardware lifecycle management',
        'PUE and efficiency improvement',
        'Tier certification compliance',
      ],
      focusAreas: ['pue', 'uptime', 'capacity', 'thermal_management', 'power_distribution'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk', 'action_items'],
      },
      decisionAuthority: ['hardware_procurement', 'capacity_expansion', 'cooling_strategy'],
      reportingTo: 'Director of Infrastructure',
      icon: 'Server',
    },
    {
      id: 'network_engineer',
      category: 'sme',
      name: 'Network Engineer',
      shortName: 'NE',
      description: 'Manages network infrastructure, switching, and connectivity',
      responsibilities: [
        'Network topology design',
        'Switch and router configuration',
        'Bandwidth optimization',
        'Network security and segmentation',
        'Traffic analysis and QoS',
      ],
      focusAreas: ['throughput', 'latency', 'packet_loss', 'bandwidth_utilization', 'uptime'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk'],
      },
      decisionAuthority: ['network_topology', 'firewall_rules', 'bandwidth_allocation'],
      reportingTo: 'IT Director',
      icon: 'Network',
    },
    {
      id: 'facilities_technician',
      category: 'technician',
      name: 'Facilities Technician',
      shortName: 'FT',
      description: 'Maintains physical infrastructure including power, cooling, and fire suppression',
      responsibilities: [
        'HVAC and CRAC maintenance',
        'UPS and generator testing',
        'PDU and busbar inspections',
        'Fire suppression system checks',
        'Physical security rounds',
      ],
      focusAreas: ['cooling_status', 'power_status', 'environmental_conditions', 'alarm_response'],
      explanationStyle: {
        depth: 'standard',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'operator',
        focusOn: ['action_items', 'safety'],
      },
      decisionAuthority: ['emergency_shutdown', 'alarm_acknowledgement', 'vendor_dispatch'],
      reportingTo: 'Facilities Manager',
      icon: 'Wrench',
    },
    // Management
    {
      id: 'infrastructure_director',
      category: 'management',
      name: 'Director of Infrastructure',
      shortName: 'DI',
      description: 'Oversees datacenter operations, capacity planning, and budget',
      responsibilities: [
        'Budget and capex planning',
        'SLA management',
        'Vendor and contract management',
        'Capacity roadmap',
        'Disaster recovery planning',
      ],
      focusAreas: ['cost_per_kw', 'sla_compliance', 'capex_roi', 'capacity_runway', 'risk_score'],
      explanationStyle: {
        depth: 'summary',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['budget_approval', 'expansion_projects', 'sla_negotiations'],
      reportingTo: 'VP of Technology',
      icon: 'BarChart',
    },
    // Executive
    {
      id: 'cto',
      category: 'executive',
      name: 'Chief Technology Officer',
      shortName: 'CTO',
      description: 'Sets technology strategy and infrastructure investment direction',
      responsibilities: [
        'Technology strategy and vision',
        'Infrastructure investment decisions',
        'Vendor ecosystem management',
        'Innovation and modernization',
        'Board reporting',
      ],
      focusAreas: ['total_cost_of_ownership', 'digital_transformation', 'sustainability', 'competitive_advantage'],
      explanationStyle: {
        depth: 'high-level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'compliance', 'risk'],
      },
      decisionAuthority: ['strategic_direction', 'major_investments', 'partnerships'],
      reportingTo: 'CEO',
      icon: 'Crown',
    },
  ],

  // =============================================================================
  // PARTS CATALOG
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'compute',
        name: 'Compute Components',
        parts: [
          { id: 'cpu_module', name: 'CPU Module', sku: 'DC-CPU-001', unitCost: 3500, leadTimeDays: 14, compatibleAssetTypes: ['compute_server'], failureModes: ['thermal_throttling', 'socket_failure'] },
          { id: 'dimm_module', name: 'Memory DIMM (64GB DDR5)', sku: 'DC-MEM-064', unitCost: 280, leadTimeDays: 7, compatibleAssetTypes: ['compute_server'], failureModes: ['bit_errors', 'electromigration'] },
          { id: 'ssd_nvme', name: 'NVMe SSD (3.84TB)', sku: 'DC-SSD-384', unitCost: 650, leadTimeDays: 5, compatibleAssetTypes: ['compute_server', 'storage_system'], failureModes: ['wear_leveling', 'controller_failure'] },
          { id: 'server_fan', name: 'Server Fan Module', sku: 'DC-FAN-001', unitCost: 45, leadTimeDays: 3, compatibleAssetTypes: ['compute_server'], failureModes: ['bearing_wear', 'motor_failure'] },
          { id: 'raid_controller', name: 'RAID Controller', sku: 'DC-RAD-001', unitCost: 850, leadTimeDays: 10, compatibleAssetTypes: ['compute_server', 'storage_system'], failureModes: ['firmware_corruption', 'cache_failure'] },
        ],
      },
      {
        id: 'power',
        name: 'Power Components',
        parts: [
          { id: 'ups_battery', name: 'UPS Battery Module (48V)', sku: 'DC-BAT-048', unitCost: 1200, leadTimeDays: 14, compatibleAssetTypes: ['power_system'], failureModes: ['capacity_loss', 'thermal_runaway'] },
          { id: 'pdu_breaker', name: 'PDU Circuit Breaker (30A)', sku: 'DC-BRK-030', unitCost: 180, leadTimeDays: 7, compatibleAssetTypes: ['power_system'], failureModes: ['trip_failure', 'contact_wear'] },
          { id: 'psu_module', name: 'Server PSU (1600W)', sku: 'DC-PSU-160', unitCost: 320, leadTimeDays: 5, compatibleAssetTypes: ['compute_server'], failureModes: ['capacitor_aging', 'fan_failure'] },
          { id: 'ats_switch', name: 'Automatic Transfer Switch', sku: 'DC-ATS-001', unitCost: 4500, leadTimeDays: 21, compatibleAssetTypes: ['power_system'], failureModes: ['transfer_failure', 'contact_degradation'] },
        ],
      },
      {
        id: 'cooling',
        name: 'Cooling Components',
        parts: [
          { id: 'crac_compressor', name: 'CRAC Compressor (scroll)', sku: 'DC-CMP-001', unitCost: 6500, leadTimeDays: 28, compatibleAssetTypes: ['cooling_unit'], failureModes: ['scroll_wear', 'refrigerant_leak'] },
          { id: 'crac_fan_motor', name: 'CRAC Fan Motor', sku: 'DC-CFM-001', unitCost: 850, leadTimeDays: 14, compatibleAssetTypes: ['cooling_unit'], failureModes: ['bearing_failure', 'winding_burn'] },
          { id: 'expansion_valve', name: 'Thermal Expansion Valve', sku: 'DC-TXV-001', unitCost: 420, leadTimeDays: 10, compatibleAssetTypes: ['cooling_unit'], failureModes: ['valve_stuck', 'calibration_drift'] },
          { id: 'air_filter', name: 'CRAC Air Filter (MERV-13)', sku: 'DC-FLT-013', unitCost: 35, leadTimeDays: 3, compatibleAssetTypes: ['cooling_unit'], failureModes: ['clogging', 'bypass_leak'] },
        ],
      },
      {
        id: 'network',
        name: 'Network Components',
        parts: [
          { id: 'sfp_transceiver', name: 'SFP+ 10G Transceiver', sku: 'DC-SFP-010', unitCost: 45, leadTimeDays: 3, compatibleAssetTypes: ['network_switch'], failureModes: ['optical_degradation', 'overheating'] },
          { id: 'qsfp_transceiver', name: 'QSFP28 100G Transceiver', sku: 'DC-QSF-100', unitCost: 280, leadTimeDays: 7, compatibleAssetTypes: ['network_switch'], failureModes: ['optical_degradation', 'lane_failure'] },
          { id: 'switch_fan', name: 'Switch Fan Tray', sku: 'DC-SFN-001', unitCost: 120, leadTimeDays: 5, compatibleAssetTypes: ['network_switch'], failureModes: ['bearing_wear', 'fan_stall'] },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE LIBRARY
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'thermal',
        name: 'Thermal Issues',
        causes: [
          { id: 'hot_aisle_containment_failure', name: 'Hot/Cold Aisle Containment Breach', severity: 'high', frequency: 'uncommon', assetTypes: ['cooling_unit', 'compute_server'] },
          { id: 'crac_failure', name: 'CRAC Unit Failure', severity: 'critical', frequency: 'rare', assetTypes: ['cooling_unit'] },
          { id: 'airflow_obstruction', name: 'Airflow Obstruction (cable management)', severity: 'medium', frequency: 'common', assetTypes: ['compute_server', 'network_switch'] },
          { id: 'thermal_paste_degradation', name: 'CPU Thermal Interface Material Degradation', severity: 'medium', frequency: 'uncommon', assetTypes: ['compute_server'] },
        ],
      },
      {
        id: 'power',
        name: 'Power Issues',
        causes: [
          { id: 'ups_battery_failure', name: 'UPS Battery End of Life', severity: 'critical', frequency: 'periodic', assetTypes: ['power_system'] },
          { id: 'pdu_overload', name: 'PDU Circuit Overload', severity: 'high', frequency: 'uncommon', assetTypes: ['power_system'] },
          { id: 'power_supply_degradation', name: 'Server PSU Capacitor Aging', severity: 'medium', frequency: 'common', assetTypes: ['compute_server'] },
          { id: 'ground_fault', name: 'Ground Fault', severity: 'critical', frequency: 'rare', assetTypes: ['power_system'] },
        ],
      },
      {
        id: 'hardware',
        name: 'Hardware Failures',
        causes: [
          { id: 'disk_failure', name: 'Storage Drive Failure (SMART alerts)', severity: 'high', frequency: 'common', assetTypes: ['compute_server', 'storage_system'] },
          { id: 'memory_ecc_errors', name: 'Memory ECC Error Accumulation', severity: 'high', frequency: 'uncommon', assetTypes: ['compute_server'] },
          { id: 'fan_bearing_failure', name: 'Fan Bearing Degradation', severity: 'medium', frequency: 'common', assetTypes: ['compute_server', 'network_switch', 'cooling_unit'] },
          { id: 'network_optics_degradation', name: 'Optical Transceiver Degradation', severity: 'medium', frequency: 'uncommon', assetTypes: ['network_switch'] },
        ],
      },
      {
        id: 'environmental',
        name: 'Environmental',
        causes: [
          { id: 'humidity_excursion', name: 'Humidity Excursion (out of ASHRAE range)', severity: 'high', frequency: 'uncommon', assetTypes: ['cooling_unit'] },
          { id: 'water_leak', name: 'Water Leak (CRAC condensate or piping)', severity: 'critical', frequency: 'rare', assetTypes: ['cooling_unit'] },
          { id: 'dust_contamination', name: 'Dust/Particulate Contamination', severity: 'medium', frequency: 'common', assetTypes: ['compute_server', 'network_switch'] },
        ],
      },
    ],
  },

  // =============================================================================
  // LABOR RATES
  // =============================================================================
  laborRates: {
    currency: 'USD',
    roles: [
      { id: 'dc_technician', name: 'Datacenter Technician', hourlyRate: 45, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'standard' },
      { id: 'dc_engineer', name: 'Datacenter Engineer', hourlyRate: 85, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'senior' },
      { id: 'network_specialist', name: 'Network Specialist', hourlyRate: 95, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'specialist' },
      { id: 'electrical_tech', name: 'Electrical Technician', hourlyRate: 55, overtimeMultiplier: 1.5, emergencyMultiplier: 2.5, skillLevel: 'standard' },
      { id: 'hvac_specialist', name: 'HVAC Specialist', hourlyRate: 75, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'specialist' },
      { id: 'vendor_oem', name: 'OEM Vendor Engineer', hourlyRate: 200, overtimeMultiplier: 1.0, emergencyMultiplier: 1.5, skillLevel: 'vendor' },
    ],
  },
}

export default datacenterConfig
